import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { parseCSV, type ParsedData } from "@/lib/csv";
import {
  getTableRowsPayload,
  type TableRowsPayloadRef,
} from "@/lib/table-rows-cache";
import { findExactColumnIndex } from "@/lib/table-utils";

export type VersionType = "generated" | "edited";

export function useSubmittalData(jobId: string) {
  const typedJobId = jobId as Id<"specPilotSubmittals">;

  // Queries
  const { data: job } = useQuery(convexQuery(api.specPilot.submittals.get, {
    submittalId: typedJobId,
  }));
  const { data: submittal } = useQuery(
    convexQuery(
      api.specPilot.generatedSubmittals.getSummaryBySubmittal,
      { submittalId: typedJobId }
    )
  );
  const { data: typicals } = useQuery(
    convexQuery(
      api.specPilot.typicalSubmittals.listBySubmittal,
      { submittalId: typedJobId }
    )
  );
  const { data: trackerItems } = useQuery(
    convexQuery(
      api.specPilot.trackerItems.listBySubmittal,
      { submittalId: typedJobId }
    )
  );
  const { data: editedPayloadRef } = useQuery(
    convexQuery(
      api.specPilot.generatedSubmittals.getEditedPayloadRef,
      submittal?.hasEditedData ? { submittalId: submittal._id } : "skip"
    )
  );
  const { data: storageUrl } = useQuery(
    convexQuery(
      api.specPilot.generatedSubmittals.getStorageUrl,
      submittal?.generatedFileStorageId || submittal?.generatedFileKey
        ? {
            storageId: submittal?.generatedFileStorageId,
            fileKey: submittal?.generatedFileKey,
          }
        : "skip"
    )
  );

  // Local state
  const [activeVersion, setActiveVersion] = useState<VersionType>("generated");
  const [generatedData, setGeneratedData] = useState<ParsedData | null>(null);
  const [editedData, setEditedData] = useState<ParsedData | null>(null);
  const [isHydratingEdited, setIsHydratingEdited] = useState(false);
  const [editedPayloadError, setEditedPayloadError] = useState<string | null>(
    null
  );
  const [isLoadingCSV, setIsLoadingCSV] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  const hasEditedData = Boolean(submittal?.hasEditedData || editedData);

  const currentData: ParsedData | null =
    activeVersion === "edited" ? editedData : generatedData;

  // Merge in typical rows not already present in the active dataset
  const displayData = useMemo((): ParsedData | null => {
    if (!currentData) return null;

    // Find the title column using multiple possible header names
    const titleCol = findExactColumnIndex(currentData.headers, ["Title", "Submittal Title"]);
    const existingTitles = new Set<string>();

    if (titleCol >= 0) {
      for (const row of currentData.rows) {
        const title = row[titleCol]?.trim();
        if (title) {
          existingTitles.add(title);
        }
      }
    }

    const typicalRows = (typicals ?? []).flatMap(
      (typical: { title: string }) => {
        if (titleCol < 0 || existingTitles.has(typical.title)) {
          return [];
        }

        const row = Array(currentData.headers.length).fill("");
        row[titleCol] = typical.title;
        return [row];
      }
    );

    return {
      headers: currentData.headers,
      rows: [...currentData.rows, ...typicalRows],
    };
  }, [currentData, typicals]);

  // Fetch and parse CSV when storage URL is available
  useEffect(() => {
    if (!storageUrl) {
      setIsLoadingCSV(false);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    async function fetchCSV() {
      setIsLoadingCSV(true);
      setCsvError(null);

      try {
        const response = await fetch(storageUrl!, { signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch CSV: ${response.status}`);
        }
        const csvText = await response.text();
        const parsed = parseCSV(csvText);
        if (!signal.aborted) {
          setGeneratedData(parsed);
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        if (!signal.aborted) {
          setCsvError(
            error instanceof Error ? error.message : "Failed to load CSV"
          );
          console.error("Error fetching CSV:", error);
        }
      } finally {
        if (!signal.aborted) {
          setIsLoadingCSV(false);
        }
      }
    }

    void fetchCSV();

    return () => {
      controller.abort();
    };
  }, [storageUrl]);

  // Hydrate edited payload
  useEffect(() => {
    let cancelled = false;

    if (editedPayloadRef === undefined) {
      return () => {
        cancelled = true;
      };
    }

    if (editedPayloadRef === null) {
      setEditedData(null);
      setEditedPayloadError(null);
      setIsHydratingEdited(false);
      return () => {
        cancelled = true;
      };
    }

    async function hydrateEditedPayload() {
      setIsHydratingEdited(true);
      setEditedPayloadError(null);
      try {
        const payload = await getTableRowsPayload(
          editedPayloadRef as TableRowsPayloadRef
        );
        if (!cancelled) {
          setEditedData(payload);
        }
      } catch (error) {
        console.error("Error loading edited submittal payload:", error);
        if (!cancelled) {
          setEditedPayloadError("Failed to load edited submittal data.");
          setEditedData(null);
        }
      } finally {
        if (!cancelled) {
          setIsHydratingEdited(false);
        }
      }
    }

    void hydrateEditedPayload();
    return () => {
      cancelled = true;
    };
  }, [editedPayloadRef]);

  // Set default version when submittal data is available
  useEffect(() => {
    setActiveVersion(submittal?.hasEditedData ? "edited" : "generated");
  }, [submittal?.hasEditedData]);

  return {
    job,
    submittal,
    typicals,
    trackerItems,
    generatedData,
    editedData,
    displayData,
    activeVersion,
    setActiveVersion,
    hasEditedData,
    isLoadingCSV,
    csvError,
    isHydratingEdited,
    editedPayloadError,
    setEditedData,
    setEditedPayloadError,
  };
}
